import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { DOMAIN_LABELS, type SatDomain } from '@/api/sat';
import { satOpenApi, type SatOpenCheckResponse, type SatOpenQuestion } from '@/api/satOpen';
import { Button } from '@/components/ui/button';
import { RichContent } from '@/components/RichContent';
import { SHORT_DOMAIN_LABELS } from '@/components/sat/journey/studentCopy';
import { MARKETING_CTAS } from '@/lib/marketingCtas';
import { recordTasterAnswer } from '@/lib/satOpenTaster';
import { cn } from '@/lib/utils';
import {
  outcomeLine,
  PROVE_IT_EXPLAINER,
  type Ladder,
  type LadderLevel,
  type LadderQuestion,
} from './homeLadderFixture';

/**
 * One rung of the difficulty ladder (HOME-V3 §B): a real bank question, answerable in
 * place. Prerender: the question is bundled text, so the stem and choices are in the static
 * HTML and nothing here touches the network or the clock at render. The only request is one
 * `POST /sat/open/check` when the visitor taps a choice (tapping IS answering: no Check
 * button, so the page keeps one primary).
 */

/** `Linear Functions · what it tests`: the skill thread reads as the label, the test as the line. */
export function LevelLine({ skill, level, className }: { skill: string; level: LadderLevel; className?: string }) {
  return (
    <p className={cn('text-foreground', className)}>
      <span className="font-semibold">{skill}</span>
      <span className="text-muted-foreground"> · {level.tests}</span>
    </p>
  );
}

export const BUSY_LINE = 'Practice is busy right now. The full diagnostic is open.';

type Phase = 'ready' | 'checking' | 'answered' | 'busy';

const isNotFound = (error: unknown) => (error as { response?: { status?: number } })?.response?.status === 404;

function fromLive(q: SatOpenQuestion): (LadderQuestion & { difficulty: string }) | null {
  const choices = (['A', 'B', 'C', 'D'] as const)
    .map((key) => ({ key, text: q[`answer${key}` as 'answerA'] }))
    .filter((c) => c.text);
  if (choices.length < 2) return null;
  return {
    key: q.questionId,
    questionId: q.questionId,
    domain: q.domain as SatDomain,
    skill: q.skillName,
    stem: q.questionText,
    choices,
    difficulty: q.difficulty,
  };
}

export interface LadderCardProps {
  ladder: Ladder;
  level: LadderLevel;
  /** The last rung offers `Another question`; the others offer `Next: {Level} →`. */
  next: LadderLevel | null;
  /** Answers already stored from earlier visits plus this one (hydration-only). */
  answeredTotal: number;
  /** The level line, repeated inside the card below `lg` where the left column is gone. */
  showLevelLine: boolean;
  /** A level answer landed (or failed to): the parent moves the ring. */
  onAnswered: (correct: boolean) => void;
  onSkillChange: (skillDiffers: boolean) => void;
  onBusyChange: (checking: boolean) => void;
  onNext: () => void;
  onStored: (total: number) => void;
}

export function LadderCard({
  ladder,
  level,
  next,
  answeredTotal,
  showLevelLine,
  onAnswered,
  onSkillChange,
  onBusyChange,
  onNext,
  onStored,
}: LadderCardProps) {
  const [question, setQuestion] = useState<LadderQuestion>(level.question);
  const [fresh, setFresh] = useState(false);
  /** A live question served by `Another question`: it no longer stands in for the level. */
  const [extra, setExtra] = useState<string | null>(null);
  const [phase, setPhase] = useState<Phase>('ready');
  const [selected, setSelected] = useState<string | null>(null);
  const [result, setResult] = useState<SatOpenCheckResponse | null>(null);
  const [loadingNext, setLoadingNext] = useState(false);
  const levelDone = useRef(false);
  const servedIds = useRef<string[]>([]);
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const setPhaseBoth = (p: Phase) => {
    setPhase(p);
    onBusyChange(p === 'checking');
  };

  const show = (nextQ: LadderQuestion, isFresh: boolean, extraDifficulty: string | null) => {
    setQuestion(nextQ);
    setFresh(isFresh);
    setExtra(extraDifficulty);
    setSelected(null);
    setResult(null);
    setPhaseBoth('ready');
    onSkillChange(isFresh && nextQ.skill !== ladder.skill);
  };

  /** The retired item's replacement: a live Algebra question, preferably at the level's difficulty. */
  const loadFresh = async (): Promise<boolean> => {
    const set = await satOpenApi.createPracticeSet({ domain: ladder.domain, excludeQuestionIds: [...servedIds.current] });
    if (!alive.current) return true;
    const mapped = set.questions.map(fromLive).filter((x): x is NonNullable<ReturnType<typeof fromLive>> => x !== null);
    const pick = mapped.find((x) => x.difficulty === level.difficulty) ?? mapped[0];
    if (!pick) return false;
    servedIds.current.push(pick.key);
    show(pick, true, null);
    return true;
  };

  const answer = async (key: string) => {
    if (phase !== 'ready') return;
    setSelected(key);
    setPhaseBoth('checking');
    try {
      const res = await satOpenApi.check(
        question.sourceCode
          ? { sourceCode: question.sourceCode, selectedKey: key }
          : { questionId: question.questionId as string, selectedKey: key },
      );
      if (!alive.current) return;
      const counts = extra === null;
      const state = recordTasterAnswer({
        questionId: question.key,
        selectedKey: key,
        correct: res.correct,
        domain: question.domain,
        answeredAt: new Date().toISOString(),
        ...(counts ? { difficulty: level.key } : {}),
        ...(question.sourceCode ? { sourceCode: question.sourceCode } : {}),
      });
      onStored(state.answers.length);
      setResult(res);
      setPhaseBoth('answered');
      if (counts && !levelDone.current) {
        levelDone.current = true;
        onAnswered(res.correct);
      }
    } catch (error) {
      if (!alive.current) return;
      if (isNotFound(error)) {
        // The question was retired: quietly swap in a live one, unanswered.
        try {
          if (await loadFresh()) return;
        } catch {
          // fall through to the busy line
        }
      }
      setPhaseBoth('busy');
    }
  };

  const another = async () => {
    if (loadingNext) return;
    setLoadingNext(true);
    try {
      const set = await satOpenApi.createPracticeSet({ domain: ladder.domain, excludeQuestionIds: [...servedIds.current] });
      if (!alive.current) return;
      const mapped = set.questions.map(fromLive).filter((x): x is NonNullable<ReturnType<typeof fromLive>> => x !== null);
      const pick = mapped[0];
      if (!pick) {
        setPhaseBoth('busy');
        return;
      }
      servedIds.current.push(pick.key);
      show(pick, false, pick.difficulty);
    } catch {
      if (alive.current) setPhaseBoth('busy');
    } finally {
      if (alive.current) setLoadingNext(false);
    }
  };

  const domainName = SHORT_DOMAIN_LABELS[question.domain] ?? DOMAIN_LABELS[question.domain];
  const levelName = extra ? (extra.charAt(0) + extra.slice(1).toLowerCase()) : level.label;
  const header = fresh
    ? `Fresh question loaded · ${domainName} · ${levelName}`
    : answeredTotal > 0
      ? `You've answered ${answeredTotal} so far.`
      : `Course question · ${domainName} · ${levelName}`;
  const correctKey = result?.correctKey;
  const shortChoices = question.choices.every((c) => c.text.length <= 10);
  const skillHidden = fresh && question.skill !== ladder.skill;

  return (
    <div
      className="rounded-2xl border bg-card p-5 shadow-[0_1px_0_hsl(var(--border)),0_24px_48px_-28px_hsl(var(--primary)/0.45)] sm:p-7"
      data-testid="ladder-card"
      data-level={level.key}
      data-subject={ladder.subject}
      data-phase={phase}
    >
      <p className="truncate text-[13px] font-medium text-muted-foreground" data-testid="ladder-header">
        {header}
      </p>

      {showLevelLine && !skillHidden && (
        <div className="mt-2 lg:hidden" data-testid="ladder-level-inline">
          <LevelLine skill={ladder.skill} level={level} className="text-sm" />
          {level.key === 'PROVE_IT' && (
            <p className="mt-1.5 text-pretty text-[13px] leading-relaxed text-muted-foreground">{PROVE_IT_EXPLAINER}</p>
          )}
        </div>
      )}

      <div className="mt-4 font-serif text-[1.075rem] leading-relaxed text-foreground sm:text-lg" data-testid="ladder-stem">
        <RichContent>{question.stem}</RichContent>
      </div>

      {phase === 'busy' ? (
        <div className="mt-5 rounded-xl bg-muted/60 p-4 ladder-in" data-testid="ladder-busy">
          <p className="text-sm text-foreground">{BUSY_LINE}</p>
          <Button asChild className="mt-3 w-full sm:w-auto">
            <Link to={MARKETING_CTAS.home.href}>{MARKETING_CTAS.home.label}</Link>
          </Button>
        </div>
      ) : (
        <div role="group" aria-label="Answer choices" className={cn('mt-5 grid gap-2.5', shortChoices && 'sm:grid-cols-2')}>
          {question.choices.map((c) => {
            const isPicked = selected === c.key;
            const decided = phase === 'answered';
            const isRight = decided && correctKey === c.key;
            const isWrongPick = decided && isPicked && !isRight;
            return (
              <button
                key={c.key}
                type="button"
                data-ladder-choice={c.key}
                disabled={phase !== 'ready'}
                aria-pressed={isPicked}
                onClick={() => void answer(c.key)}
                className={cn(
                  'relative flex min-h-12 w-full items-center gap-3 overflow-hidden rounded-xl border-2 bg-background px-3 py-2 text-left text-[15px] transition-colors',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-card',
                  phase === 'ready' && 'hover:border-primary/60 hover:bg-primary/5',
                  phase === 'checking' && isPicked && 'ladder-shimmer border-primary/60 bg-primary/5',
                  phase === 'checking' && !isPicked && 'opacity-60',
                  isRight && 'border-emerald-500 bg-emerald-500/10',
                  isWrongPick && 'border-rose-500 bg-rose-500/10',
                  decided && !isRight && !isWrongPick && 'opacity-60',
                )}
              >
                <span
                  className={cn(
                    'flex h-7 w-7 shrink-0 items-center justify-center rounded-full border text-[13px] font-semibold',
                    isRight
                      ? 'border-emerald-500 bg-emerald-500 text-white'
                      : isWrongPick
                        ? 'border-rose-500 bg-rose-500 text-white'
                        : 'border-foreground/25 text-foreground/80',
                  )}
                >
                  {c.key}
                </span>
                <span className="min-w-0 flex-1">
                  <RichContent>{c.text}</RichContent>
                </span>
              </button>
            );
          })}
        </div>
      )}

      {phase === 'answered' && result && (
        <div className="mt-5 ladder-in" aria-live="polite" data-testid="ladder-result">
          <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
            <p
              className={cn(
                'text-base font-semibold',
                result.correct ? 'text-emerald-700 dark:text-emerald-400' : 'text-rose-700 dark:text-rose-400',
              )}
            >
              {result.correct ? '✓ Correct' : `✗ Not quite. The answer is ${result.correctKey}`}
            </p>
            {result.explanation && (
              <details className="group text-sm text-muted-foreground">
                <summary className="flex cursor-pointer list-none items-center gap-1 rounded font-medium text-foreground/80 outline-none marker:hidden focus-visible:ring-2 focus-visible:ring-primary [&::-webkit-details-marker]:hidden">
                  Why?
                  <ChevronRight className="h-3.5 w-3.5 transition-transform group-open:rotate-90" aria-hidden />
                </summary>
                <div className="mt-2 max-w-prose basis-full text-sm leading-relaxed text-muted-foreground">
                  <RichContent>{result.explanation}</RichContent>
                </div>
              </details>
            )}
          </div>
          <p className="mt-2 text-pretty text-sm leading-snug text-foreground/90" data-testid="ladder-outcome">
            {extra ? (result.correct ? 'Right. One more rung of practice.' : 'Not yet. Practice would aim right here.') : outcomeLine(level.key, result.correct, SHORT_DOMAIN_LABELS[ladder.domain])}
          </p>

          <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:items-center">
            <Button asChild className="w-full sm:w-auto">
              <Link to={MARKETING_CTAS.home.href}>{MARKETING_CTAS.home.label}</Link>
            </Button>
            {next && !extra ? (
              <Button type="button" variant="ghost" className="w-full sm:w-auto" onClick={onNext}>
                Next: {next.label} →
              </Button>
            ) : (
              <Button type="button" variant="ghost" className="w-full sm:w-auto" onClick={() => void another()} disabled={loadingNext}>
                Another question
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
